import { useState } from "react";
import { QUIZZES, QUIZ_CATEGORIES } from "@/data/parentingData";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import Mascot from "@/components/Mascot";

export default function QuizPage() {
  const [activeId, setActiveId] = useState(null);
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [category, setCategory] = useState("Tous");

  const quiz = QUIZZES.find((q) => q.id === activeId);
  const visible = category === "Tous" ? QUIZZES : QUIZZES.filter((q) => q.category === category);

  const start = (id) => {
    setActiveId(id); setStep(0); setScore(0); setDone(false);
  };
  const answer = (val) => {
    const newScore = score + val;
    setScore(newScore);
    if (step + 1 >= quiz.questions.length) {
      setDone(true);
      toast.success("Quiz terminé !");
    } else {
      setStep(step + 1);
    }
  };
  const reset = () => { setActiveId(null); setDone(false); setStep(0); setScore(0); };

  if (!activeId) {
    return (
      <div className="space-y-6" data-testid="parental-quiz-section">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>Quiz parentaux</h1>
            <p className="mt-2 text-[#77849C]">
              {QUIZZES.length} auto-évaluations pour découvrir ta posture, sans jugement.
            </p>
          </div>
          <Mascot pose="idea" size={92} anim="float" className="hidden sm:block shrink-0 mascot-pop" />
        </div>

        <div className="filter-row no-scrollbar" data-testid="quiz-category-filters">
          {["Tous", ...QUIZ_CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`quiz-cat-${c}`}
              onClick={() => setCategory(c)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                category === c
                  ? "bg-[#23375E] text-white"
                  : "bg-white border border-[#E3E8F1] text-[#44506B] hover:border-[#23375E]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]" data-testid="quiz-count">
          {visible.length} quiz
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((q) => (
            <div key={q.id} data-testid={`quiz-card-${q.id}`} className="soft-card p-6 flex flex-col">
              <h3 className="text-xl font-semibold text-[#16233D]">{q.title}</h3>
              <p className="mt-2 text-[#44506B] flex-1">{q.description}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="age-badge pastel-sage">{q.category}</span>
                <span className="age-badge pastel-butter">{q.questions.length} questions</span>
                {q.ages && <span className="age-badge pastel-sky">{q.ages}</span>}
              </div>
              <button data-testid={`start-quiz-${q.id}`} onClick={() => start(q.id)} className="mt-4 brand-btn rounded-full px-5 py-2 self-start">
                Commencer
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (done) {
    const fb = quiz.feedback.find((f) => score >= f.min && score <= f.max) || quiz.feedback[0];
    return (
      <div className="max-w-xl mx-auto soft-card p-8 text-center fade-up" data-testid="quiz-result">
        <CheckCircle2 className="mx-auto h-12 w-12 text-[#23375E]" />
        <h2 className="mt-4 text-2xl font-bold text-[#16233D]">{fb.title}</h2>
        <p className="mt-2 text-lg text-[#44506B]">Score : {score}</p>
        <p className="mt-4 text-[#44506B] leading-relaxed">{fb.text}</p>
        <button data-testid="reset-quiz-btn" onClick={reset} className="mt-6 inline-flex items-center gap-2 brand-btn rounded-full px-5 py-2">
          <RotateCcw className="h-4 w-4" /> Faire un autre quiz
        </button>
      </div>
    );
  }

  const q = quiz.questions[step];
  const progress = ((step + 1) / quiz.questions.length) * 100;

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="quiz-active">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]">{quiz.title}</p>
        <div className="mt-2 h-2 bg-[#E3E8F1] rounded-full overflow-hidden">
          <div className="h-full bg-[#23375E] transition-all" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-2 text-xs text-[#77849C]">Question {step + 1} / {quiz.questions.length}</p>
      </div>
      <div className="soft-card p-6 fade-up">
        <h3 className="text-xl font-semibold text-[#16233D]">{q.q}</h3>
        <div className="mt-5 space-y-3">
          {q.options.map((opt, i) => (
            <button key={i} data-testid={`quiz-opt-${i}`} onClick={() => answer(opt.score)}
              className="w-full text-left rounded-2xl border border-[#E3E8F1] bg-white p-4 hover:border-[#23375E] hover:bg-[#EDF2F9] transition">
              <span className="text-[#16233D]">{opt.text}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
